/**
 * Campaign create/edit — full page (replaces the old "New Campaign" dialog).
 * 2 tabs: Message composer + Settings (when/who).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Tooltip,
  Paper,
  Grid,
  Checkbox,
  FormGroup,
  IconButton,
  Autocomplete,
  LinearProgress,
  ListSubheader,
} from '@mui/material';
import {
  ArrowBack,
  AccountTree,
  Schedule,
  TouchApp,
  NotificationsActive,
  People,
  CheckCircleOutline,
  GroupsOutlined,
  Add,
  Close,
  AutoAwesome,
  ArrowDropDown,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import adminApi from '@/services/admin.api';
import WhatsAppPreviewBubble from './WhatsAppPreviewBubble';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { isPermissionLockTagged } from '@/utils/permission-lock.util';
import { formatTemplateName } from '@/utils/whatsapp-template-media.util';
import TemplatePickerPopover from '@/components/admin/TemplatePickerPopover';
import { ADVANCED_FILTER_OPTIONS } from '@/constants/advancedContactFilters';
import {
  CAMPAIGN_REACH_STATUS_OPTIONS,
  AUDIENCE_SEND_ORDER_OPTIONS,
  AudienceSendOrder,
} from '@/constants/campaignAudienceSlice';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Agent {
  _id: string;
  name: string;
}

interface CampaignOption {
  _id: string;
  name: string;
  status?: string;
  trigger?: { type?: string };
}

interface SegmentFilter {
  tags?: string[];
  countryCodes?: string[];
  status?: string;
  agent?: string;
  customAttributes?: Array<{ key: string; value: string }>;
  /** Tier 1 WhatsApp delivery/engagement filter — same resolver the Contacts
   *  page's filter drawer uses (see contact-filter.service.ts). */
  advanced?: { key: string; errorCode?: number; campaignId?: string };
}

/** Optional ordering/position/reach-status layer applied on top of the base
 *  targeting filter above — see Campaign.model.ts's ICampaignAudienceSlice. */
interface AudienceSlice {
  reachStatus?:
    | 'any'
    | 'neverTargeted'
    | 'targetedAny'
    | 'targetedPending'
    | 'targetedDelivered'
    | 'targetedNotDelivered';
  sortBy?: 'dateAdded' | 'lastEngagement' | 'random';
  sortDir?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

interface SegmentOptions {
  tags: string[];
  countryCodes: string[];
  statuses: string[];
  agents: Agent[];
  customAttributeKeys: string[];
}

interface WhatsAppTemplateLite {
  _id: string;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: string;
  components: {
    header?: {
      type: string;
      text?: string;
      example?: { header_handle?: string[] };
      headerMediaUrl?: string;
      headerMediaId?: string;
      headerMediaS3Key?: string;
      headerVariableLabel?: string;
    };
    // Optional: AUTHENTICATION templates (excluded from every picker in this
    // file, but the type shouldn't lie about what the API can actually return).
    body?: { text: string; variableLabels?: string[] };
    footer?: { text: string };
    buttons?: Array<{ type: string; text: string; url?: string; example?: string }>;
    // MARKETING/UTILITY carousel — mutually exclusive with header/footer/buttons above.
    carousel?: {
      cards: Array<{
        header?: { type: string; headerMediaUrl?: string };
        body?: { text: string };
        buttons?: Array<{ type: string; text: string; url?: string }>;
      }>;
    };
  };
}

interface Campaign {
  _id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  type: 'automated' | 'bulk' | 'event-driven';
  trigger: {
    type:
      | 'newContact'
      | 'inactivity'
      | 'firstMessage'
      | 'conversationResolved'
      | 'followUp'
      | 'manual';
    delay: number;
    conditions: Array<{ field: string; operator: string; value: any }>;
    agentId?: string;
    followUpScope?: 'new' | 'all';
    maxAttempts?: number;
  };
  targeting: {
    filterType: 'all' | 'segment' | 'savedSegment' | 'agent' | 'tag' | 'country' | 'contacts';
    filterValue?: string | string[] | SegmentFilter;
    excludeTags?: string[];
    audienceSlice?: AudienceSlice;
  };
  messageTemplate: {
    mode?: 'text' | 'template';
    templateText: string;
    templateName?: string;
    templateLanguage?: string;
    variables: string[];
    headerMediaVariable?: string;
    buttonUrlVariable?: string;
  };
  schedule: {
    type: 'immediate' | 'scheduled' | 'recurring';
    startDate?: string;
    endDate?: string;
    cronExpression?: string;
    timezone?: string;
    maxRuns?: number;
    retryConfig?: { enabled: boolean; maxRetries: number; delaysMinutes: number[] };
  };
  limits?: {
    maxRecipients?: number;
    dailyLimit?: number;
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VARIABLES: Array<{ key: string; label: string }> = [
  { key: 'customerName', label: 'Name' },
  { key: 'customerPhone', label: 'Phone' },
  { key: 'customerEmail', label: 'Email' },
  { key: 'customerCountry', label: 'Country' },
  { key: 'customerCountryCode', label: 'Country code' },
  { key: 'customerCompany', label: 'Company (lead)' },
  { key: 'title', label: 'Deal/enquiry title (lead)' },
  { key: 'description', label: 'Enquiry description (lead)' },
  { key: 'budget', label: 'Budget (lead)' },
  { key: 'interest', label: 'Interest (lead)' },
  { key: 'estimatedValue', label: 'Estimated value (lead)' },
  // Only resolve when this send is genuinely tied to an appointment (a workflow triggered
  // by appointment.created/status_changed, or — for a plain bulk/scheduled send — this
  // contact's most recently updated appointment). Unlike the lead fields above, these are
  // NOT always available; an unresolved appointment field blocks the send with a clear
  // alert rather than mailing out a literal "[appointment:meetingLink]" (see
  // APPOINTMENT_VAR_PREFIX handling in campaign-send.service.ts).
  { key: 'appointment:requestedAt', label: 'Appointment date/time (appointment)' },
  { key: 'appointment:requestedText', label: 'Appointment date/time — raw phrase (appointment)' },
  { key: 'appointment:title', label: 'Appointment title (appointment)' },
  { key: 'appointment:appointmentType', label: 'Appointment type (appointment)' },
  { key: 'appointment:meetingLink', label: 'Meeting link (appointment)' },
  { key: 'appointment:reference', label: 'Booking reference (appointment)' },
  { key: 'appointment:status', label: 'Appointment status (appointment)' },
  { key: 'appointment:timezone', label: 'Appointment timezone (appointment)' },
];

// Prefix used to mark a template variable slot as a fixed literal value sent
// to every recipient (e.g. "static:20" for a "Percentage" slot). Must match
// STATIC_VALUE_PREFIX in backend/src/services/campaign-send.service.ts.
const STATIC_VALUE_PREFIX = 'static:';
// Prefix used to bind a template variable slot to one of the lead's own
// schema-free custom fields (Lead.model.ts's `customAttributes`), e.g.
// "custom:dealId". Must match CUSTOM_ATTR_PREFIX in
// backend/src/services/campaign-send.service.ts.
const CUSTOM_ATTR_PREFIX = 'custom:';
// Prefix identifying the fixed set of Appointment-specific fields above (e.g.
// "appointment:meetingLink") — distinct from CUSTOM_ATTR_PREFIX since these are a known,
// hardcoded field set, not tenant-defined. Must match APPOINTMENT_VAR_PREFIX in
// backend/src/services/campaign-send.service.ts.
const APPOINTMENT_VAR_PREFIX = 'appointment:';
// Sentinel option value for the "Custom value (same for everyone)" choice in
// the variable-mapping select — never persisted as-is.
const CUSTOM_VALUE_OPTION = '__custom__';

const PREVIEW_VALUES: Record<string, string> = {
  customerName: 'John Doe',
  phone: '+1 555-0123',
  customerPhone: '+1 555-0123',
  customerEmail: 'john@example.com',
  customerCountry: 'United States',
  customerCountryCode: 'US',
  customerCompany: 'Acme Inc.',
  title: '6-Day Andaman Island Hopping',
  description: 'Interested in a 6-day island-hopping package.',
  budget: '$5,000',
  interest: 'Enterprise plan',
  estimatedValue: '5000',
  'appointment:requestedAt': '12 Sep 2026, 2:00 pm',
  'appointment:requestedText': 'tomorrow afternoon',
  'appointment:title': 'Renewal discussion',
  'appointment:appointmentType': 'Product demo',
  'appointment:meetingLink': 'https://meet.google.com/abc-defg-hij',
  'appointment:reference': 'BOOK-9821',
  'appointment:status': 'confirmed',
  'appointment:timezone': 'Asia/Kolkata',
};

// Labels for the "Segment breakdown" sidebar's "Recipient type" row.
const RECIPIENT_TYPE_LABELS: Record<string, string> = {
  all: 'All contacts',
  agent: 'By agent',
  tag: 'By tag',
  country: 'By country',
  segment: 'Custom segment',
  savedSegment: 'Saved segment',
  contacts: 'Specific contacts',
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_CRON = [1, 2, 3, 4, 5, 6, 0]; // cron: 0=Sun,1=Mon...

// Number of positional {{1}}, {{2}}... placeholders in a Meta template body.
function countTemplateVariables(text: string): number {
  const found = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) {
    found.add(parseInt(m[1] || '0', 10));
  }
  return found.size;
}

/** Does this template have a URL button with a `{{n}}` (dynamic) suffix —
 *  the "Download Invoice"-style button pattern? There's no dedicated
 *  "download" button type in WhatsApp's API; it's just a URL button whose
 *  link is filled in per-recipient at send time. */
function hasDynamicUrlButton(template: WhatsAppTemplateLite | undefined): boolean {
  return !!template?.components.buttons?.some(
    (b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || '')
  );
}

/**
 * A single-value "map this to a contact field" dropdown — the same
 * VARIABLES/lead-custom-attribute options as the per-{{n}} body variable
 * select above it, but for a single slot (header media, or a dynamic URL
 * button) rather than one of several body placeholders. No "custom value
 * (same for everyone)" option: if the same value belongs on every message,
 * that's just the template's own static default — leaving this unmapped.
 */
function DynamicValueSelect({
  label,
  helperText,
  value,
  leadCustomAttributeKeys,
  onChange,
}: {
  label: string;
  helperText: string;
  value: string;
  leadCustomAttributeKeys: string[];
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      helperText={helperText}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <MenuItem value="">
        <em>Use the template's own default (same for everyone)</em>
      </MenuItem>
      {VARIABLES.map(({ key, label: fieldLabel }) => (
        <MenuItem key={key} value={key}>
          {fieldLabel}
        </MenuItem>
      ))}
      {leadCustomAttributeKeys.length > 0 && [
        <ListSubheader key="__lead_custom_header">Lead custom fields</ListSubheader>,
        ...leadCustomAttributeKeys.map((attrKey) => (
          <MenuItem key={`${CUSTOM_ATTR_PREFIX}${attrKey}`} value={`${CUSTOM_ATTR_PREFIX}${attrKey}`}>
            {attrKey}
          </MenuItem>
        )),
      ]}
    </TextField>
  );
}

// Suggest a contact-field key for a template author's variable label
// (e.g. "Customer name" -> "customerName"), based on keyword matching.
function suggestVariableKey(label: string): string {
  const l = (label || '').toLowerCase();
  if (!l.trim()) return '';
  if (/country code|country_code|isd/.test(l)) return 'customerCountryCode';
  if (/country/.test(l)) return 'customerCountry';
  if (/phone|mobile|whatsapp|contact number/.test(l)) return 'customerPhone';
  if (/email|e-mail/.test(l)) return 'customerEmail';
  if (/company|organi[sz]ation/.test(l)) return 'customerCompany';
  if (/budget/.test(l)) return 'budget';
  if (/interest/.test(l)) return 'interest';
  if (/meeting link|zoom|meet link|video call/.test(l)) return `${APPOINTMENT_VAR_PREFIX}meetingLink`;
  if (/booking ref|reference/.test(l)) return `${APPOINTMENT_VAR_PREFIX}reference`;
  if (/appointment date|appointment time|when|date.?time/.test(l)) return `${APPOINTMENT_VAR_PREFIX}requestedAt`;
  if (/name/.test(l)) return 'customerName';
  return '';
}

// Render a template body for preview, substituting {{1}}, {{2}}... with the
// preview values of the contact fields mapped to each slot.
function renderTemplatePreview(text: string, variableMap: string[]): string {
  return (text || '').replace(/\{\{(\d+)\}\}/g, (_, idx) => {
    const i = parseInt(idx, 10) - 1;
    const key = variableMap[i];
    if (!key) return `[${idx}]`;
    if (key.startsWith(STATIC_VALUE_PREFIX)) {
      const literal = key.slice(STATIC_VALUE_PREFIX.length);
      return literal || `[${idx}]`;
    }
    if (key.startsWith(CUSTOM_ATTR_PREFIX)) {
      return `[${key.slice(CUSTOM_ATTR_PREFIX.length)}]`;
    }
    return PREVIEW_VALUES[key] ?? `[${key}]`;
  });
}

/** A short, practical timezone list for recurring-campaign scheduling. IST first
 *  (default for this India-heavy tenant base), then UTC and a few common zones.
 *  Values are IANA names passed verbatim to the backend cron resolver. */
const CAMPAIGN_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'Asia/Kolkata', label: 'India (IST, UTC+5:30)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Dubai', label: 'Gulf (GST, UTC+4)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'America/New_York', label: 'US Eastern (ET)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (PT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT, UTC+8)' },
];

/** AiSensy-style default spacing for a NEW retry round appended to the list —
 *  the last existing round's delay minus an hour, floored at 1h so it's never
 *  zero/negative. Mirrors the 12h→11h→10h taper without hardcoding a 4th value. */
function defaultNextRetryDelay(existing: number[]): number {
  const last = existing[existing.length - 1];
  return Math.max(60, (last ?? 780) - 60);
}

function minutesToHm(totalMinutes: number): { hours: number; minutes: number } {
  const clamped = Math.max(0, Math.round(totalMinutes));
  return { hours: Math.floor(clamped / 60), minutes: clamped % 60 };
}

function hmToMinutes(hours: number, minutes: number): number {
  return Math.max(0, Math.round(hours) || 0) * 60 + Math.max(0, Math.min(59, Math.round(minutes) || 0));
}

function toCronExpression(repeat: string, days: number[], time: string): string {
  const parts = time.split(':').map(Number);
  const h = isNaN(parts[0] ?? NaN) ? 9 : (parts[0] ?? 9);
  const m = isNaN(parts[1] ?? NaN) ? 0 : (parts[1] ?? 0);
  if (repeat === 'weekly') {
    const ds = days.length ? days.join(',') : '1';
    return `${m} ${h} * * ${ds}`;
  }
  if (repeat === 'monthly') return `${m} ${h} 1 * *`;
  return `${m} ${h} * * *`; // daily
}

// ─── Send method helpers ──────────────────────────────────────────────────────

type SendMethod = 'workflow' | 'manual' | 'scheduled' | 'behavior';
type BehaviorType = 'inactivity' | 'followup';
type RepeatType = 'once' | 'daily' | 'weekly' | 'monthly';

// ─── Initial form state ───────────────────────────────────────────────────────

function emptyForm() {
  return {
    name: '',
    description: '',
    // Message — campaigns always send an approved WhatsApp template (Meta
    // requires this for business-initiated sends).
    messageMode: 'template' as const,
    templateName: '',
    templateLanguage: '',
    templateVariableMap: [] as string[],
    // Per-recipient header media (e.g. a different invoice PDF per customer)
    // and dynamic URL button value (e.g. a "Download Invoice" link) — same
    // key convention as templateVariableMap entries, empty = use the
    // template's own static default.
    headerMediaVariable: '',
    buttonUrlVariable: '',
    // Settings — how to send
    sendMethod: 'workflow' as SendMethod,
    scheduleDate: '',
    scheduleTime: '09:00',
    repeatType: 'once' as RepeatType,
    repeatDays: [] as number[], // weekday cron numbers
    repeatTime: '09:00',
    // IANA tz the repeatTime is interpreted in. Default IST (most tenants are
    // India-based); the backend interprets the cron in exactly this zone, so the
    // fire time is identical no matter which server/timezone runs the worker.
    repeatTimezone: 'Asia/Kolkata',
    // Stop conditions for a recurring campaign (both optional; blank = run forever).
    // endDate = last date it may fire; maxRuns = cap on total occurrences. The worker
    // (campaign-scheduler.worker.ts scheduleNextOrStop) honors both.
    repeatEndDate: '',
    repeatMaxRuns: '' as number | '',
    // Tier 3 auto-retry: re-send retryable failures (chiefly frequency-capping)
    // as delayed follow-up runs. Opt-in. delaysMinutes[i] = how long AFTER the
    // previous run/retry completes before retry round i+1 fires — one editable
    // row per round, length kept in sync with retryMaxRetries. Default matches
    // AiSensy's spacing (12h/11h/10h) but every value is user-editable.
    retryEnabled: false,
    retryMaxRetries: 3,
    retryDelaysMinutes: [720, 660, 600] as number[],
    behaviorType: 'inactivity' as BehaviorType,
    inactivityDays: 3,
    // Follow-up delay is stored on the backend in MINUTES; the form lets the
    // user pick the unit. `followupValue` is the number they type, interpreted
    // by `followupUnit`.
    followupValue: 24,
    followupUnit: 'hours' as 'minutes' | 'hours',
    // Follow-up audience scope: 'new' (default) never nudges the pre-existing
    // backlog of quiet chats when the campaign is switched on; 'all' considers
    // every eligible conversation regardless of age.
    followupScope: 'new' as 'new' | 'all',
    // Max automated follow-up nudges per contact. Conservative default of 1 — a
    // sales enquiry re-pinged repeatedly reads as spam and hurts WhatsApp quality.
    followupMaxAttempts: 1,
    // Settings — who receives it
    targetFilter: 'all' as
      | 'all'
      | 'agent'
      | 'tag'
      | 'country'
      | 'segment'
      | 'savedSegment'
      | 'contacts',
    targetAgentId: '',
    targetTagList: [] as string[],
    targetCountries: '',
    targetContacts: [] as Array<{ phone: string; name?: string }>,
    // A saved segment from the Contacts page's filter drawer — the full Tier 1-3
    // filter set (delivery status, engagement, source, assignment, quality risk,
    // etc.), reused as-is rather than rebuilt as a smaller ad-hoc filter here.
    targetSavedSegmentId: '',
    excludeTags: [] as string[],
    // Segment (composite) targeting
    segmentTags: [] as string[],
    segmentCountries: [] as string[],
    segmentStatus: '',
    segmentAgent: '',
    segmentCustomAttributes: [] as Array<{ key: string; value: string }>,
    // Tier 1 WhatsApp delivery/engagement filter, layered onto the segment above.
    segmentAdvanced: 'all',
    segmentAdvancedErrorCode: '',
    segmentAdvancedCampaignId: '',
    // Audience slice — ordering/position/reach-status layered on top of whichever
    // recipients filter is selected above (e.g. "the first 50 by date added" or
    // "the ones this campaign hasn't reached yet"). Only meaningful for one-time
    // sends — hidden in the UI (and rejected server-side) for recurring campaigns.
    audienceReachStatus: 'any' as NonNullable<AudienceSlice['reachStatus']>,
    // One plain-English choice standing in for the backend's separate sortBy/sortDir
    // pair — see AUDIENCE_SEND_ORDER_OPTIONS.
    audienceSendOrder: 'dateAdded_asc' as AudienceSendOrder,
    audienceOffset: '',
    audienceLimit: '',
    // "Skip" is rarely needed (only for sending sequential batches of the same
    // slice) — kept out of the way behind an "Advanced" toggle in the UI.
    showAudienceAdvanced: false,
    // Safety ceiling — never send to more than this many, regardless of ordering.
    maxRecipients: '',
    // Per-day send throttle — blank falls back to the plan's campaignDailyLimitDefault.
    dailyLimit: '',
  };
}

/**
 * Builds the `targeting.audienceSlice` payload from form state, or `undefined`
 * when nothing has actually been customized — so a campaign nobody touched
 * this feature on resolves EXACTLY as before (no sort/skip imposed at all,
 * see campaign-send.service.ts's getTargetContacts).
 */
function buildAudienceSliceValue(f: ReturnType<typeof emptyForm>): AudienceSlice | undefined {
  const offset = parseInt(f.audienceOffset, 10);
  const limit = parseInt(f.audienceLimit, 10);
  const isDefault =
    (f.audienceReachStatus ?? 'any') === 'any' &&
    f.audienceSendOrder === 'dateAdded_asc' &&
    !Number.isInteger(offset) &&
    !Number.isInteger(limit);
  if (isDefault) return undefined;

  const order = AUDIENCE_SEND_ORDER_OPTIONS.find((opt) => opt.value === f.audienceSendOrder);

  return {
    reachStatus: f.audienceReachStatus,
    sortBy: order?.sortBy ?? 'dateAdded',
    sortDir: order?.sortDir ?? 'asc',
    ...(Number.isInteger(offset) && offset > 0 ? { offset } : {}),
    ...(Number.isInteger(limit) && limit > 0 ? { limit } : {}),
  };
}

/**
 * Builds the ad-hoc `segment` targeting filter (`targetFilter === 'segment'`)
 * from form state — shared by the live reach-estimate and the submit payload
 * so the two can never drift on what "the current filter" means.
 */
function buildSegmentFilterValue(f: ReturnType<typeof emptyForm>): SegmentFilter {
  const segment: SegmentFilter = {};
  if (f.segmentTags.length > 0) segment.tags = f.segmentTags;
  if (f.segmentCountries.length > 0) segment.countryCodes = f.segmentCountries;
  if (f.segmentStatus) segment.status = f.segmentStatus;
  if (f.segmentAgent) segment.agent = f.segmentAgent;
  const customAttributes = f.segmentCustomAttributes
    .map((field) => ({ key: field.key.trim(), value: field.value.trim() }))
    .filter((field) => field.key);
  if (customAttributes.length > 0) segment.customAttributes = customAttributes;

  // Mirrors the Contacts page's "don't send an advanced key whose required
  // sub-value isn't filled in yet" rule (isAdvancedFilterSet).
  if (f.segmentAdvanced === 'failedByError') {
    const code = parseInt(f.segmentAdvancedErrorCode.trim(), 10);
    if (Number.isInteger(code)) segment.advanced = { key: f.segmentAdvanced, errorCode: code };
  } else if (f.segmentAdvanced === 'neverDeliveredThisCampaign') {
    if (f.segmentAdvancedCampaignId) {
      segment.advanced = { key: f.segmentAdvanced, campaignId: f.segmentAdvancedCampaignId };
    }
  } else if (f.segmentAdvanced !== 'all') {
    segment.advanced = { key: f.segmentAdvanced };
  }

  return segment;
}

/**
 * Numbered step header for the Recipient Filters tab — makes the filter
 * pipeline read as an explicit sequence (pick a base audience → narrow it →
 * exclude specific people → cap the total) instead of a flat list of
 * same-weight fields with no ordering cue. Steps 2-4 are visually lighter
 * (outlined badge) since they're optional narrowing on top of Step 1's base
 * audience, not equally-weighted alternatives to it.
 */
function StepHeader({
  step,
  title,
  description,
  optional,
}: {
  step: number;
  title: string;
  description?: string;
  optional?: boolean;
}): React.ReactElement {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
      <Box
        sx={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          mt: 0.1,
          ...(optional
            ? { border: '1.5px solid', borderColor: 'text.disabled', color: 'text.secondary' }
            : { bgcolor: 'primary.main', color: 'primary.contrastText' }),
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
          {optional && (
            <Typography component="span" variant="caption" color="text.secondary" fontWeight={400}>
              {'  ·  optional'}
            </Typography>
          )}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const CampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editMode = Boolean(id);
  const { guard } = usePlanGate();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplateLite[]>([]);
  const [templatePickerAnchor, setTemplatePickerAnchor] = useState<HTMLElement | null>(null);
  const [segmentOptions, setSegmentOptions] = useState<SegmentOptions>({
    tags: [],
    countryCodes: [],
    statuses: [],
    agents: [],
    customAttributeKeys: [],
  });
  const [savedSegments, setSavedSegments] = useState<
    Array<{ _id: string; name: string; description?: string }>
  >([]);
  // Distinct Lead customAttributes.key values in use by this tenant (Leads.tsx's own
  // custom fields, NOT Conversation/Contact customAttributeKeys above — a workflow-
  // triggered campaign resolves template variables against the Lead, so only Lead
  // custom-attribute keys are valid `custom:<key>` binding targets here).
  const [leadCustomAttributeKeys, setLeadCustomAttributeKeys] = useState<string[]>([]);
  // Populates the campaign picker shown for the segment builder's "Never
  // delivered — this campaign" advanced filter (excludes the campaign being
  // edited itself — it can't sensibly reference its own not-yet-sent history).
  const [otherCampaigns, setOtherCampaigns] = useState<CampaignOption[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(editMode);
  const [submitting, setSubmitting] = useState(false);

  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState(emptyForm());

  // Signed preview URL for the selected template's header media (image/video/document).
  const [headerMediaPreview, setHeaderMediaPreview] = useState<{
    previewUrl: string | null;
    mimeType?: string;
    fileName?: string;
  } | null>(null);
  const [headerMediaPreviewLoading, setHeaderMediaPreviewLoading] = useState(false);
  // Same, but one signed URL per card, for a CAROUSEL template.
  const [carouselMediaPreview, setCarouselMediaPreview] = useState<
    Array<{ previewUrl: string | null; fileName?: string }>
  >([]);
  const [carouselMediaPreviewLoading, setCarouselMediaPreviewLoading] = useState(false);

  // Estimated number of contacts the current targeting filter would reach.
  const [reach, setReach] = useState<number | null>(null);
  const [reachLoading, setReachLoading] = useState(false);
  // Tenant's total contact count, fetched once — powers the "Segment breakdown"
  // sidebar's progress bar (estimated match as a fraction of the whole database).
  const [totalDatabaseCount, setTotalDatabaseCount] = useState<number | null>(null);

  // Contact picker (for "Specific contacts") — search results from existing conversations.
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState<Array<{ phone: string; name?: string }>>([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // These reads stay open even when campaignsEnabled is off (see campaign.routes.ts)
    // so the form still shows real data — only the submit button is gated.
    fetchAgents();
    fetchTemplates();
    fetchSegmentOptions();
    fetchLeadCustomAttributeKeys();
    fetchSavedSegments();
    fetchOtherCampaigns();
    fetchTotalDatabaseCount();
    if (editMode && id) fetchCampaign(id);
  }, []);

  // Live-estimate how many contacts the current targeting filter reaches.
  useEffect(() => {
    const targeting: any = { filterType: formData.targetFilter };
    if (formData.targetFilter === 'agent')
      targeting.filterValue = formData.targetAgentId || undefined;
    if (formData.targetFilter === 'tag') targeting.filterValue = formData.targetTagList;
    if (formData.targetFilter === 'country')
      targeting.filterValue = formData.targetCountries
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (formData.targetFilter === 'contacts')
      targeting.filterValue = formData.targetContacts.map((c) => c.phone);
    if (formData.targetFilter === 'segment')
      targeting.filterValue = buildSegmentFilterValue(formData);
    if (formData.targetFilter === 'savedSegment')
      targeting.filterValue = formData.targetSavedSegmentId || undefined;
    if (formData.excludeTags.length) targeting.excludeTags = formData.excludeTags;
    const audienceSlice = buildAudienceSliceValue(formData);
    if (audienceSlice) targeting.audienceSlice = audienceSlice;
    const maxRecipients = parseInt(formData.maxRecipients, 10);
    const limits =
      Number.isInteger(maxRecipients) && maxRecipients > 0 ? { maxRecipients } : undefined;

    // A savedSegment with nothing chosen yet isn't a real filter — skip the
    // estimate call rather than send a request the backend would 400 on
    // (mirrors how the Contacts page never sends a filter still missing its
    // required sub-value).
    if (formData.targetFilter === 'savedSegment' && !formData.targetSavedSegmentId) {
      setReach(null);
      return;
    }

    const handle = setTimeout(async () => {
      setReachLoading(true);
      try {
        // `campaignId` (edit mode only) lets the estimate resolve `audienceSlice.reachStatus`
        // against THIS campaign's real send history rather than a throwaway id — see
        // campaign.service.ts's estimateReach.
        const reach = await adminApi.estimateReach(targeting, limits, editMode ? id : undefined);
        setReach(reach);
      } catch {
        setReach(null);
      } finally {
        setReachLoading(false);
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [
    formData.targetFilter,
    formData.targetAgentId,
    formData.targetTagList,
    formData.targetCountries,
    formData.targetContacts,
    formData.segmentTags,
    formData.segmentCountries,
    formData.segmentStatus,
    formData.segmentAgent,
    formData.segmentCustomAttributes,
    formData.targetSavedSegmentId,
    formData.excludeTags,
    formData.segmentAdvanced,
    formData.segmentAdvancedErrorCode,
    formData.segmentAdvancedCampaignId,
    formData.audienceReachStatus,
    formData.audienceSendOrder,
    formData.audienceOffset,
    formData.audienceLimit,
    formData.maxRecipients,
  ]);

  // Search existing conversations by name/phone for the "Specific contacts" picker.
  useEffect(() => {
    if (formData.targetFilter !== 'contacts') return;
    const q = contactSearch.trim();
    if (!q) {
      setContactOptions([]);
      return;
    }

    const handle = setTimeout(async () => {
      setContactSearchLoading(true);
      try {
        const { data: list } = await adminApi.getConversations({ search: q, limit: 20 });
        setContactOptions(
          list
            .filter((c) => c.customerPhone)
            .map((c) =>
              c.customerName
                ? { phone: c.customerPhone, name: c.customerName }
                : { phone: c.customerPhone }
            )
        );
      } catch {
        setContactOptions([]);
      } finally {
        setContactSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [contactSearch, formData.targetFilter]);

  // When the selected template (in template mode) has a media header with a
  // stored S3 copy, fetch a signed preview URL for the composer.
  useEffect(() => {
    if (
      formData.messageMode !== 'template' ||
      !formData.templateName ||
      !formData.templateLanguage
    ) {
      setHeaderMediaPreview(null);
      setCarouselMediaPreview([]);
      return;
    }
    const selected = templates.find(
      (t) => t.name === formData.templateName && t.language === formData.templateLanguage
    );

    const cards = selected?.components.carousel?.cards;
    if (cards && cards.length > 0) {
      setHeaderMediaPreview(null);
      fetchCarouselMediaPreview(formData.templateName, formData.templateLanguage);
      return;
    }
    setCarouselMediaPreview([]);

    const header = selected?.components.header;
    const isMediaHeader = header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
    if (!isMediaHeader || !(header.headerMediaS3Key || header.headerMediaId)) {
      setHeaderMediaPreview(null);
      return;
    }
    fetchHeaderMediaPreview(formData.templateName, formData.templateLanguage);
  }, [formData.messageMode, formData.templateName, formData.templateLanguage, templates]);

  const fetchCampaign = async (campaignId: string) => {
    setLoading(true);
    try {
      const campaign = await adminApi.getCampaignById(campaignId);
      populateFromCampaign(campaign);
    } catch (e: any) {
      if (!isPermissionLockTagged(e)) {
        setError(e?.response?.data?.error?.message || e.message || 'Failed to load campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const { data } = await adminApi.getAgents({ limit: 100 }, { suppressPermissionLock: true });
      setAgents(data);
    } catch {
      /* non-critical */
    }
  };

  const fetchLeadCustomAttributeKeys = async () => {
    try {
      const keys = await adminApi.getLeadAttributeKeys({ suppressPermissionLock: true });
      setLeadCustomAttributeKeys(keys ?? []);
    } catch {
      /* non-critical */
    }
  };

  const fetchSegmentOptions = async () => {
    try {
      const options = await adminApi.getSegmentOptions({ suppressPermissionLock: true });
      setSegmentOptions({
        tags: options?.tags ?? [],
        countryCodes: options?.countryCodes ?? [],
        statuses: options?.statuses ?? [],
        agents: options?.agents ?? [],
        customAttributeKeys: options?.customAttributeKeys ?? [],
      });
    } catch {
      /* non-critical */
    }
  };

  const fetchSavedSegments = async () => {
    try {
      const segments = await adminApi.getSegments({ suppressPermissionLock: true });
      setSavedSegments(segments ?? []);
    } catch {
      /* non-critical */
    }
  };

  // Total contact count for the tenant, unfiltered — purely for the sidebar's
  // "Total database" stat and progress bar; non-critical if it fails.
  // Deliberately NOT getConversations() (the Contacts page's list endpoint):
  // that endpoint fetches at most 5000 documents before computing its
  // meta.total from the fetched array's length, so it silently plateaus at
  // 5000 for larger tenants. This dedicated endpoint runs a direct
  // countDocuments instead (see campaign.service.ts's getTotalContactCount).
  const fetchTotalDatabaseCount = async () => {
    try {
      const count = await adminApi.getTotalContactCount({ suppressPermissionLock: true });
      setTotalDatabaseCount(count);
    } catch {
      /* non-critical */
    }
  };

  const fetchOtherCampaigns = async () => {
    try {
      const list = (await adminApi.getCampaigns(
        { limit: 100 },
        { suppressPermissionLock: true }
      )) as CampaignOption[];
      setOtherCampaigns(id ? list.filter((c) => c._id !== id) : list);
    } catch {
      /* non-critical */
    }
  };

  const fetchTemplates = async () => {
    try {
      const list = (await adminApi.getWhatsAppTemplates({
        suppressPermissionLock: true,
      })) as unknown as WhatsAppTemplateLite[];
      // AUTHENTICATION templates have no free-text body (Meta generates fixed
      // OTP copy) and Meta's own policy bars them from marketing/broadcast use
      // — they're excluded from every "send via template" picker in this app.
      setTemplates(list.filter((t) => t.status === 'APPROVED' && t.category !== 'AUTHENTICATION'));
    } catch {
      /* non-critical */
    }
  };

  // Fetch a short-lived signed S3 URL to preview the selected template's
  // header media (image/video/document) in the composer. Preview-only —
  // sending still uses Meta's headerMediaId, never this URL.
  const fetchHeaderMediaPreview = async (name: string, language: string) => {
    setHeaderMediaPreviewLoading(true);
    setHeaderMediaPreview(null);
    try {
      const preview = await adminApi.getWhatsAppTemplateHeaderMediaPreview(name, language, {
        suppressPermissionLock: true,
      });
      setHeaderMediaPreview(preview);
    } catch {
      /* non-critical */
    } finally {
      setHeaderMediaPreviewLoading(false);
    }
  };

  // Same as `fetchHeaderMediaPreview`, but for every card of a CAROUSEL template.
  const fetchCarouselMediaPreview = async (name: string, language: string) => {
    setCarouselMediaPreviewLoading(true);
    setCarouselMediaPreview([]);
    try {
      const preview = await adminApi.getWhatsAppTemplateCarouselMediaPreview(name, language, {
        suppressPermissionLock: true,
      });
      setCarouselMediaPreview(preview);
    } catch {
      /* non-critical */
    } finally {
      setCarouselMediaPreviewLoading(false);
    }
  };

  // ── Populate form from an existing campaign ─────────────────────────────────

  const populateFromCampaign = (c: Campaign) => {
    // Defensive defaults: campaigns created before `trigger`/`schedule`/
    // `targeting`/`messageTemplate` were required fields may have these
    // sub-objects missing entirely on the stored document.
    const trigger = c.trigger ?? { type: 'manual', delay: 0, conditions: [] };
    const targeting = c.targeting ?? { filterType: 'all' };
    const messageTemplate = c.messageTemplate ?? { templateText: '', variables: [] };
    const schedule = c.schedule ?? { type: 'immediate' };

    let sendMethod: SendMethod = 'manual';
    let behaviorType: BehaviorType = 'inactivity';
    let repeatType: RepeatType = 'once';
    let repeatDays: number[] = [];

    if (c.type === 'event-driven') sendMethod = 'workflow';
    else if (trigger.type === 'inactivity') {
      sendMethod = 'behavior';
      behaviorType = 'inactivity';
    } else if (trigger.type === 'followUp') {
      sendMethod = 'behavior';
      behaviorType = 'followup';
    } else if (schedule.type === 'scheduled' || schedule.type === 'recurring')
      sendMethod = 'scheduled';
    else sendMethod = 'manual';

    if (schedule.cronExpression) {
      const parts = schedule.cronExpression.split(' ');
      const dayPart = parts[4] ?? '*';
      if (dayPart !== '*') {
        repeatType = 'weekly';
        repeatDays = dayPart.split(',').map(Number);
      } else if (parts[2] !== '*') repeatType = 'monthly';
      else repeatType = 'daily';
    } else if (schedule.type === 'scheduled') {
      repeatType = 'once';
    }

    const cronTime = (() => {
      if (!schedule.cronExpression) return '09:00';
      const p = schedule.cronExpression.split(' ');
      return `${String(p[1] ?? 9).padStart(2, '0')}:${String(p[0] ?? 0).padStart(2, '0')}`;
    })();

    setFormData({
      name: c.name,
      description: c.description ?? '',
      messageMode: 'template',
      templateName: messageTemplate.templateName ?? '',
      templateLanguage: messageTemplate.templateLanguage ?? '',
      templateVariableMap:
        messageTemplate.mode === 'template' ? (messageTemplate.variables ?? []) : [],
      headerMediaVariable: messageTemplate.headerMediaVariable ?? '',
      buttonUrlVariable: messageTemplate.buttonUrlVariable ?? '',
      sendMethod,
      scheduleDate: schedule.startDate ? schedule.startDate.slice(0, 16) : '',
      scheduleTime: '09:00',
      repeatType,
      repeatDays,
      repeatTime: cronTime,
      // Restore the stored tz so an edit re-saves it unchanged; default IST for
      // campaigns created before tz support existed (matches the backend default).
      repeatTimezone: schedule.timezone || 'Asia/Kolkata',
      repeatEndDate: schedule.endDate ? String(schedule.endDate).slice(0, 10) : '',
      repeatMaxRuns: schedule.maxRuns ?? '',
      retryEnabled: Boolean(schedule.retryConfig?.enabled),
      retryMaxRetries: schedule.retryConfig?.maxRetries ?? 3,
      retryDelaysMinutes:
        schedule.retryConfig?.delaysMinutes?.length ? schedule.retryConfig.delaysMinutes : [720, 660, 600],
      behaviorType,
      inactivityDays: trigger.type === 'inactivity' ? (trigger.delay ?? 3) : 3,
      // Restore the stored minutes into the friendliest unit: minutes when the
      // delay is under an hour or not a whole number of hours, otherwise hours.
      followupValue:
        trigger.type === 'followUp'
          ? (trigger.delay ?? 1440) % 60 !== 0 || (trigger.delay ?? 1440) < 60
            ? (trigger.delay ?? 1440)
            : Math.round((trigger.delay ?? 1440) / 60)
          : 24,
      followupUnit: (trigger.type === 'followUp' &&
      ((trigger.delay ?? 1440) % 60 !== 0 || (trigger.delay ?? 1440) < 60)
        ? 'minutes'
        : 'hours') as 'minutes' | 'hours',
      followupScope: (trigger.followUpScope ?? 'new') as 'new' | 'all',
      followupMaxAttempts: trigger.maxAttempts ?? 1,
      targetFilter: targeting.filterType ?? 'all',
      targetAgentId:
        trigger.agentId ??
        (targeting.filterType === 'agent' ? String(targeting.filterValue ?? '') : ''),
      targetTagList:
        targeting.filterType === 'tag'
          ? Array.isArray(targeting.filterValue)
            ? targeting.filterValue
            : [String(targeting.filterValue ?? '')].filter(Boolean)
          : [],
      targetCountries:
        targeting.filterType === 'country'
          ? Array.isArray(targeting.filterValue)
            ? targeting.filterValue.join(', ')
            : String(targeting.filterValue ?? '')
          : '',
      targetContacts:
        targeting.filterType === 'contacts'
          ? (Array.isArray(targeting.filterValue)
              ? targeting.filterValue
              : [String(targeting.filterValue ?? '')]
            )
              .filter(Boolean)
              .map((phone) => ({ phone: String(phone) }))
          : [],
      excludeTags: targeting.excludeTags ?? [],
      segmentTags:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.tags ?? [])
          : [],
      segmentCountries:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.countryCodes ?? [])
          : [],
      segmentStatus:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.status ?? '')
          : '',
      segmentAgent:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.agent ?? '')
          : '',
      segmentCustomAttributes:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.customAttributes ?? [])
          : [],
      segmentAdvanced:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.advanced?.key ?? 'all')
          : 'all',
      segmentAdvancedErrorCode:
        targeting.filterType === 'segment'
          ? String((targeting.filterValue as SegmentFilter)?.advanced?.errorCode ?? '')
          : '',
      segmentAdvancedCampaignId:
        targeting.filterType === 'segment'
          ? ((targeting.filterValue as SegmentFilter)?.advanced?.campaignId ?? '')
          : '',
      targetSavedSegmentId:
        targeting.filterType === 'savedSegment' ? String(targeting.filterValue ?? '') : '',
      audienceReachStatus: targeting.audienceSlice?.reachStatus ?? 'any',
      audienceSendOrder:
        (AUDIENCE_SEND_ORDER_OPTIONS.find(
          (opt) =>
            opt.sortBy === (targeting.audienceSlice?.sortBy ?? 'dateAdded') &&
            opt.sortDir === (targeting.audienceSlice?.sortDir ?? 'asc')
        )?.value ?? 'dateAdded_asc') as AudienceSendOrder,
      audienceOffset: targeting.audienceSlice?.offset ? String(targeting.audienceSlice.offset) : '',
      audienceLimit: targeting.audienceSlice?.limit ? String(targeting.audienceSlice.limit) : '',
      showAudienceAdvanced: Boolean(targeting.audienceSlice?.offset),
      maxRecipients: c.limits?.maxRecipients ? String(c.limits.maxRecipients) : '',
      dailyLimit: c.limits?.dailyLimit ? String(c.limits.dailyLimit) : '',
    });
  };

  // ── Build backend payload ────────────────────────────────────────────────────

  const buildPayload = () => {
    const f = formData;

    // Derive type
    const type =
      f.sendMethod === 'workflow'
        ? 'event-driven'
        : f.sendMethod === 'behavior'
          ? 'automated'
          : f.sendMethod === 'scheduled'
            ? 'automated'
            : 'bulk';

    // Derive trigger
    const trigger = {
      type:
        f.sendMethod === 'behavior' && f.behaviorType === 'inactivity'
          ? 'inactivity'
          : f.sendMethod === 'behavior' && f.behaviorType === 'followup'
            ? 'followUp'
            : 'manual',
      delay:
        f.sendMethod === 'behavior' && f.behaviorType === 'inactivity'
          ? f.inactivityDays
          : f.sendMethod === 'behavior' && f.behaviorType === 'followup'
            ? f.followupUnit === 'minutes'
              ? f.followupValue
              : f.followupValue * 60
            : 0,
      conditions: [],
      agentId: f.targetFilter === 'agent' && f.targetAgentId ? f.targetAgentId : undefined,
      // Follow-up-only audience controls. Scope defaults to 'new' (backend also
      // defaults it); maxAttempts is sent only for follow-up so inactivity keeps
      // its built-in default cap.
      followUpScope:
        f.sendMethod === 'behavior' && f.behaviorType === 'followup' ? f.followupScope : undefined,
      maxAttempts:
        f.sendMethod === 'behavior' && f.behaviorType === 'followup'
          ? Number(f.followupMaxAttempts) || 1
          : undefined,
    };

    // Derive schedule
    let schedule: any = { type: 'immediate' };
    if (f.sendMethod === 'scheduled') {
      if (f.repeatType === 'once') {
        schedule = {
          type: 'scheduled',
          startDate: f.scheduleDate ? new Date(f.scheduleDate).toISOString() : undefined,
        };
      } else {
        schedule = {
          type: 'recurring',
          cronExpression: toCronExpression(f.repeatType, f.repeatDays, f.repeatTime),
          // The cron's HH:MM is local wall-clock; this tells the backend which
          // timezone to interpret it in so the fire time is server-independent.
          timezone: f.repeatTimezone || 'Asia/Kolkata',
          // Optional recurring stop conditions (omitted when blank = run forever).
          // endDate uses end-of-day so a campaign still fires ON the chosen date.
          ...(f.repeatEndDate
            ? { endDate: new Date(`${f.repeatEndDate}T23:59:59`).toISOString() }
            : {}),
          ...(f.repeatMaxRuns ? { maxRuns: Number(f.repeatMaxRuns) } : {}),
        };
      }
    }
    // Tier 3 auto-retry lives on `schedule` and applies to whatever run this
    // campaign produces (immediate/scheduled/recurring). Only sent when enabled,
    // so an untouched campaign carries no retryConfig (back-compat).
    if (f.retryEnabled) {
      // One delay per round the user configured — no longer a fixed literal.
      // maxRetries is derived from the row count so the two can never disagree.
      const delaysMinutes = f.retryDelaysMinutes.length ? f.retryDelaysMinutes : [720];
      schedule.retryConfig = {
        enabled: true,
        maxRetries: Math.min(3, Math.max(1, delaysMinutes.length)),
        delaysMinutes,
      };
    }

    // Derive targeting
    const targeting: any = { filterType: f.targetFilter };
    if (f.targetFilter === 'agent') targeting.filterValue = f.targetAgentId || undefined;
    if (f.targetFilter === 'tag') targeting.filterValue = f.targetTagList;
    if (f.targetFilter === 'country')
      targeting.filterValue = f.targetCountries
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (f.targetFilter === 'contacts') targeting.filterValue = f.targetContacts.map((c) => c.phone);
    if (f.targetFilter === 'segment') targeting.filterValue = buildSegmentFilterValue(f);
    if (f.targetFilter === 'savedSegment')
      targeting.filterValue = f.targetSavedSegmentId || undefined;
    if (f.excludeTags.length) targeting.excludeTags = f.excludeTags;
    // Audience slice is hidden (and never built) for recurring campaigns — see the
    // "Audience selection" block's visibility condition below and the matching
    // server-side rejection in campaign.service.ts's assertAudienceSliceValid.
    const isRecurring = f.sendMethod === 'scheduled' && f.repeatType !== 'once';
    const audienceSlice = isRecurring ? undefined : buildAudienceSliceValue(f);
    if (audienceSlice) targeting.audienceSlice = audienceSlice;

    // Derive limits — a safety cap, independent of (and floored against) the
    // audience-slice `limit` above.
    const maxRecipients = parseInt(f.maxRecipients, 10);
    const dailyLimit = parseInt(f.dailyLimit, 10);
    const limitsObj: { maxRecipients?: number; dailyLimit?: number } = {};
    if (Number.isInteger(maxRecipients) && maxRecipients > 0) limitsObj.maxRecipients = maxRecipients;
    if (Number.isInteger(dailyLimit) && dailyLimit > 0) limitsObj.dailyLimit = dailyLimit;
    const limits = Object.keys(limitsObj).length ? limitsObj : undefined;

    // Derive message template — always an approved WhatsApp template.
    const selected = templates.find(
      (t) => t.name === f.templateName && t.language === f.templateLanguage
    );
    const messageTemplate: any = {
      mode: 'template',
      templateText: selected?.components.body?.text || '',
      templateName: f.templateName,
      templateLanguage: f.templateLanguage,
      variables: f.templateVariableMap,
      headerMediaVariable: f.headerMediaVariable || undefined,
      buttonUrlVariable: f.buttonUrlVariable || undefined,
    };

    return {
      name: f.name,
      description: f.description || undefined,
      type,
      status: 'active',
      trigger,
      targeting,
      messageTemplate,
      schedule,
      ...(limits ? { limits } : {}),
    };
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      if (editMode && id) {
        await adminApi.updateCampaign(id, buildPayload());
      } else {
        await adminApi.createCampaign(buildPayload());
      }
      setSuccess(editMode ? 'Campaign updated!' : 'Campaign created and active!');
      setTimeout(() => navigate('/admin/campaigns'), 900);
    } catch (e: any) {
      if (!showPlanLockIfTagged(e) && !isPermissionLockTagged(e)) {
        setError(e?.response?.data?.error?.message || e.message || 'Operation failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key: keyof ReturnType<typeof emptyForm>, value: any) =>
    setFormData((p) => ({ ...p, [key]: value }));

  // Only one active follow-up campaign is allowed per workspace (see backend
  // enforcement). `otherCampaigns` already excludes the campaign being edited, so
  // this is a genuine OTHER active follow-up. When present, the "No reply after"
  // option is locked unless it's already the selected trigger (editing a draft
  // follow-up must stay editable).
  const existingActiveFollowUp = otherCampaigns.find(
    (c) => c.trigger?.type === 'followUp' && c.status === 'active'
  );
  const followUpLocked =
    Boolean(existingActiveFollowUp) && formData.behaviorType !== 'followup';

  // Toggle a value in/out of a string[] form field (used for segment checkboxes)
  const toggleListValue = (
    key: 'segmentTags' | 'segmentCountries',
    value: string,
    checked: boolean
  ) => {
    setFormData((p) => ({
      ...p,
      [key]: checked ? [...p[key], value] : p[key].filter((v) => v !== value),
    }));
  };

  const addSegmentCustomAttribute = () => {
    setFormData((p) => ({
      ...p,
      segmentCustomAttributes: [...p.segmentCustomAttributes, { key: '', value: '' }],
    }));
  };

  const updateSegmentCustomAttribute = (
    index: number,
    patch: Partial<{ key: string; value: string }>
  ) => {
    setFormData((p) => {
      const current = p.segmentCustomAttributes[index];
      if (!current) return p;
      const next = [...p.segmentCustomAttributes];
      next[index] = { key: patch.key ?? current.key, value: patch.value ?? current.value };
      return { ...p, segmentCustomAttributes: next };
    });
  };

  const removeSegmentCustomAttribute = (index: number) => {
    setFormData((p) => ({
      ...p,
      segmentCustomAttributes: p.segmentCustomAttributes.filter((_, i) => i !== index),
    }));
  };

  // Audience slicing only makes sense against a fixed, one-time resolution of the
  // audience — a recurring campaign would resolve the exact same slice on every
  // occurrence. Hidden here; rejected server-side too (assertAudienceSliceValid).
  const isRecurringCampaign = formData.sendMethod === 'scheduled' && formData.repeatType !== 'once';

  // "Send once on a date" requires a valid, parseable date/time — without this,
  // buildPayload() silently omits schedule.startDate and the backend rejects the
  // save (or, pre-validation, would have saved a campaign that can never fire).
  const isScheduleOnce = formData.sendMethod === 'scheduled' && formData.repeatType === 'once';
  const scheduleOnceDateValid =
    !isScheduleOnce ||
    (Boolean(formData.scheduleDate) && !Number.isNaN(new Date(formData.scheduleDate).getTime()));

  const canSubmit =
    Boolean(formData.name.trim()) &&
    Boolean(formData.templateName) &&
    formData.templateVariableMap.every((v) =>
      v.startsWith(STATIC_VALUE_PREFIX) ? v.length > STATIC_VALUE_PREFIX.length : Boolean(v)
    ) &&
    (formData.targetFilter !== 'contacts' || formData.targetContacts.length > 0) &&
    (formData.targetFilter !== 'savedSegment' || Boolean(formData.targetSavedSegmentId)) &&
    // A "Custom segment" with nothing configured resolves to {} and would target
    // every contact — require at least one criterion (buildSegmentFilterValue is
    // the same builder the reach estimate + payload use, so this never drifts).
    (formData.targetFilter !== 'segment' ||
      Object.keys(buildSegmentFilterValue(formData)).length > 0) &&
    (formData.audienceSendOrder !== 'random' ||
      isRecurringCampaign ||
      Number(formData.audienceLimit) > 0) &&
    scheduleOnceDateValid;

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton onClick={() => navigate('/admin/campaigns')} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          {editMode ? 'Edit Campaign' : 'New Campaign'}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mb: 2 }}>
        <Tab label="Message" />
        <Tab label="Triggers" />
        <Tab label="Recipient Filters" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        {/* ── Tab 0: Message ─────────────────────────────────────────────── */}
        {tab === 0 && (
          <Stack spacing={2.5}>
            {/* Name + description */}
            <TextField
              label="Campaign name"
              required
              fullWidth
              autoFocus
              value={formData.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Welcome New Lead, Monthly Promo, Re-engagement"
            />
            <TextField
              label="Internal description (optional)"
              fullWidth
              value={formData.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Remind yourself what this campaign is for"
              size="small"
            />

            <Divider />

            {/* Message type: approved WhatsApp templates only — Meta requires a
                pre-approved template for any business-initiated campaign send
                (free-text is only allowed inside an existing 24h session). */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                Message
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Campaigns send an approved WhatsApp template, as required by Meta for
                business-initiated messages.
              </Typography>
            </Box>

            {(() => {
              /* Approved template picker + variable mapping + unified preview */
              return (
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    {templates.length === 0 ? (
                      <Alert severity="info">
                        No approved templates yet. Go to Settings → WhatsApp → Message Templates to
                        create one and wait for Meta's approval.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {/* Same picker as Chat Logs — search + category chips, newest
                            first, media-type icons, and media-unfit templates shown
                            unselectable with an edit shortcut. */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Template"
                          placeholder="Select a template"
                          value={
                            formData.templateName ? formatTemplateName(formData.templateName) : ''
                          }
                          onClick={(e) => setTemplatePickerAnchor(e.currentTarget)}
                          InputProps={{
                            readOnly: true,
                            endAdornment: <ArrowDropDown sx={{ color: 'action.active' }} />,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': { cursor: 'pointer' },
                            '& input': { cursor: 'pointer' },
                          }}
                        />
                        <TemplatePickerPopover
                          anchorEl={templatePickerAnchor}
                          open={Boolean(templatePickerAnchor)}
                          onClose={() => setTemplatePickerAnchor(null)}
                          templates={templates}
                          editUrlFor={(t) =>
                            `${window.location.pathname.startsWith('/admin') ? '/admin' : ''}` +
                            `/whatsapp-templates/${encodeURIComponent(t.name)}/edit` +
                            `?language=${encodeURIComponent(t.language)}`
                          }
                          onSelect={(selected) => {
                            const count = countTemplateVariables(selected.components.body?.text || '');
                            const labels = selected.components.body?.variableLabels || [];
                            setFormData((p) => ({
                              ...p,
                              templateName: selected.name,
                              templateLanguage: selected.language,
                              templateVariableMap: Array.from(
                                { length: count },
                                (_, i) =>
                                  p.templateVariableMap[i] || suggestVariableKey(labels[i] || '')
                              ),
                              // A mapping from the previous template rarely still applies —
                              // different template, possibly no media header / no dynamic
                              // button at all. Carrying it over silently risks a confusing
                              // "this template has no dynamic button" rejection at activation
                              // time for a field the user can no longer even see in the UI.
                              headerMediaVariable: '',
                              buttonUrlVariable: '',
                            }));
                          }}
                        />

                        {formData.templateName &&
                          (() => {
                            const selected = templates.find(
                              (t) =>
                                t.name === formData.templateName &&
                                t.language === formData.templateLanguage
                            );
                            if (!selected) return null;
                            const varCount = countTemplateVariables(selected.components.body?.text || '');
                            const header = selected.components.header;
                            const isMediaHeader =
                              header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
                            return (
                              <>
                                {header?.type === 'TEXT' && header.text && (
                                  <Alert severity="info" variant="outlined">
                                    This template has a text header: "{header.text}"
                                  </Alert>
                                )}
                                {isMediaHeader &&
                                  !formData.headerMediaVariable &&
                                  !(
                                    header.headerMediaS3Key ||
                                    header.headerMediaId ||
                                    header.headerMediaUrl
                                  ) && (
                                    <Alert severity="warning" variant="outlined">
                                      This template has a {header.type.toLowerCase()} header but no
                                      media is uploaded — sending will fail. Upload the header media
                                      on the Message Templates page first, or map "Header media" below
                                      to send a different file per recipient instead.
                                    </Alert>
                                  )}

                                {isMediaHeader && (
                                  <DynamicValueSelect
                                    label="Header media"
                                    helperText={`Optional — send a different ${header!.type.toLowerCase()} per recipient (e.g. each customer's own invoice) instead of the one uploaded on the template.`}
                                    value={formData.headerMediaVariable}
                                    leadCustomAttributeKeys={leadCustomAttributeKeys}
                                    onChange={(value) =>
                                      setFormData((p) => ({ ...p, headerMediaVariable: value }))
                                    }
                                  />
                                )}

                                <Paper variant="outlined" sx={{ p: 1.5, background: '#f9fafb' }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Template body (read-only — approved by Meta, cannot be edited
                                    here)
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'monospace',
                                      fontSize: 13,
                                    }}
                                  >
                                    {selected.components.body?.text}
                                  </Typography>
                                </Paper>

                                {varCount > 0 && (
                                  <Stack spacing={1.5}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      Map template variables
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      For each placeholder, either pull a value from the contact's
                                      details or send the same custom value to everyone.
                                    </Typography>
                                    {Array.from({ length: varCount }, (_, i) => {
                                      const rawValue = formData.templateVariableMap[i] || '';
                                      const isStatic = rawValue.startsWith(STATIC_VALUE_PREFIX);
                                      const selectValue = isStatic ? CUSTOM_VALUE_OPTION : rawValue;
                                      const staticLiteral = isStatic
                                        ? rawValue.slice(STATIC_VALUE_PREFIX.length)
                                        : '';
                                      return (
                                        <Stack
                                          key={i}
                                          direction="row"
                                          spacing={1}
                                          alignItems="flex-start"
                                        >
                                          <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={`{{${i + 1}}}`}
                                            helperText={
                                              selected.components.body?.variableLabels?.[i] || ''
                                            }
                                            value={selectValue}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setFormData((p) => {
                                                const map = [...p.templateVariableMap];
                                                map[i] =
                                                  value === CUSTOM_VALUE_OPTION
                                                    ? STATIC_VALUE_PREFIX
                                                    : value;
                                                return { ...p, templateVariableMap: map };
                                              });
                                            }}
                                          >
                                            {VARIABLES.map(({ key, label }) => (
                                              <MenuItem key={key} value={key}>
                                                {label}
                                              </MenuItem>
                                            ))}
                                            {leadCustomAttributeKeys.length > 0 && [
                                              <ListSubheader key="__lead_custom_header">
                                                Lead custom fields
                                              </ListSubheader>,
                                              ...leadCustomAttributeKeys.map((attrKey) => (
                                                <MenuItem
                                                  key={`${CUSTOM_ATTR_PREFIX}${attrKey}`}
                                                  value={`${CUSTOM_ATTR_PREFIX}${attrKey}`}
                                                >
                                                  {attrKey}
                                                </MenuItem>
                                              )),
                                            ]}
                                            <MenuItem value={CUSTOM_VALUE_OPTION}>
                                              Custom value (same for everyone)
                                            </MenuItem>
                                          </TextField>
                                          {isStatic && (
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label="Value sent to everyone"
                                              placeholder="e.g. 20"
                                              value={staticLiteral}
                                              onChange={(e) => {
                                                const literal = e.target.value;
                                                setFormData((p) => {
                                                  const map = [...p.templateVariableMap];
                                                  map[i] = `${STATIC_VALUE_PREFIX}${literal}`;
                                                  return { ...p, templateVariableMap: map };
                                                });
                                              }}
                                            />
                                          )}
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}

                                {hasDynamicUrlButton(selected) && (
                                  <DynamicValueSelect
                                    label={`"${selected.components.buttons!.find((b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || ''))!.text}" button link`}
                                    helperText="Required — this template's button links to a per-recipient URL (e.g. each customer's own invoice)."
                                    value={formData.buttonUrlVariable}
                                    leadCustomAttributeKeys={leadCustomAttributeKeys}
                                    onChange={(value) =>
                                      setFormData((p) => ({ ...p, buttonUrlVariable: value }))
                                    }
                                  />
                                )}
                              </>
                            );
                          })()}
                      </Stack>
                    )}
                  </Grid>

                  {/* Preview — single WhatsApp-style bubble (media + body + buttons) */}
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                      Preview
                    </Typography>
                    {(() => {
                      const selected = templates.find(
                        (t) =>
                          t.name === formData.templateName &&
                          t.language === formData.templateLanguage
                      );
                      if (!selected) {
                        return (
                          <WhatsAppPreviewBubble
                            bodyText=""
                            emptyHint="Select a template to preview it here…"
                          />
                        );
                      }
                      const header = selected.components.header;
                      const isMediaHeader =
                        header && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type);
                      const mediaUrl = isMediaHeader
                        ? headerMediaPreview?.previewUrl || header.headerMediaUrl || null
                        : null;
                      const cards = selected.components.carousel?.cards;
                      return (
                        <WhatsAppPreviewBubble
                          mediaUrl={mediaUrl}
                          mediaType={
                            isMediaHeader
                              ? (header!.type as 'IMAGE' | 'VIDEO' | 'DOCUMENT')
                              : undefined
                          }
                          mediaFileName={headerMediaPreview?.fileName}
                          mediaLoading={isMediaHeader && headerMediaPreviewLoading}
                          bodyText={renderTemplatePreview(
                            selected.components.body?.text || '',
                            formData.templateVariableMap
                          )}
                          footerText={selected.components.footer?.text}
                          buttons={selected.components.buttons}
                          carousel={
                            cards && cards.length > 0
                              ? cards.map((card, i) => ({
                                  mediaUrl:
                                    carouselMediaPreview[i]?.previewUrl ||
                                    card.header?.headerMediaUrl ||
                                    null,
                                  mediaType: card.header?.type as 'IMAGE' | 'VIDEO' | undefined,
                                  mediaFileName: carouselMediaPreview[i]?.fileName,
                                  mediaLoading: carouselMediaPreviewLoading,
                                  bodyText: renderTemplatePreview(
                                    card.body?.text || '',
                                    formData.templateVariableMap
                                  ),
                                  buttons: card.buttons,
                                }))
                              : undefined
                          }
                        />
                      );
                    })()}
                    <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                      Preview uses sample values: John Doe, +1 555-0123…
                    </Typography>
                  </Grid>
                </Grid>
              );
            })()}
          </Stack>
        )}

        {/* ── Tab 1: Triggers (when + how it runs, incl. auto-retry) ──────── */}
        {tab === 1 && (
          <Stack spacing={3}>
            {/* ── When / how to run ──────────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                How will this campaign run?
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Choose when and how this campaign gets sent.
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={formData.sendMethod}
                  onChange={(e) => set('sendMethod', e.target.value as SendMethod)}
                >
                  {/* Via Workflow */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: formData.sendMethod === 'workflow' ? 'primary.main' : 'divider',
                      bgcolor: formData.sendMethod === 'workflow' ? 'primary.50' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="workflow"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <AccountTree fontSize="small" color="primary" />
                            <Typography fontWeight={600}>Via Automation Workflow</Typography>
                            <Chip
                              label="Recommended"
                              size="small"
                              color="primary"
                              sx={{ ml: 0.5 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" mt={0.25}>
                            This campaign is triggered by a node in your Automation Workflow canvas.
                            Go to Automation → Workflows and add a "Send Campaign" node.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', my: 0 }}
                    />
                  </Paper>

                  {/* Manual */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: formData.sendMethod === 'manual' ? 'primary.main' : 'divider',
                      bgcolor: formData.sendMethod === 'manual' ? 'primary.50' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="manual"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <TouchApp fontSize="small" color="action" />
                            <Typography fontWeight={600}>Send manually</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" mt={0.25}>
                            You click "Send" from the campaigns list whenever you're ready to send.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', my: 0 }}
                    />
                  </Paper>

                  {/* Scheduled */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: formData.sendMethod === 'scheduled' ? 'primary.main' : 'divider',
                      bgcolor: formData.sendMethod === 'scheduled' ? 'primary.50' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="scheduled"
                      control={<Radio size="small" />}
                      label={
                        <Box width="100%">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Schedule fontSize="small" color="action" />
                            <Typography fontWeight={600}>On a schedule</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" mt={0.25} mb={1.5}>
                            Send once at a specific time, or repeat daily / weekly / monthly.
                          </Typography>
                          {formData.sendMethod === 'scheduled' && (
                            <Box ml={0}>
                              <TextField
                                select
                                size="small"
                                label="Repeat"
                                value={formData.repeatType}
                                onChange={(e) => set('repeatType', e.target.value as RepeatType)}
                                sx={{ width: 180, mr: 2, mb: 2 }}
                              >
                                <MenuItem value="once">Send once on a date</MenuItem>
                                <MenuItem value="daily">Every day</MenuItem>
                                <MenuItem value="weekly">Every week on…</MenuItem>
                                <MenuItem value="monthly">Every month on the 1st</MenuItem>
                              </TextField>

                              {formData.repeatType === 'once' && (
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <DateTimePicker
                                    label="Date and time"
                                    value={formData.scheduleDate ? new Date(formData.scheduleDate) : null}
                                    onChange={(newValue: Date | null) =>
                                      set(
                                        'scheduleDate',
                                        newValue && !Number.isNaN(newValue.getTime())
                                          ? newValue.toISOString()
                                          : ''
                                      )
                                    }
                                    disablePast
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        sx: { width: 240 },
                                        error: !scheduleOnceDateValid,
                                        helperText: !scheduleOnceDateValid
                                          ? 'Pick a date and time to schedule this send'
                                          : ' ',
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              )}

                              {formData.repeatType !== 'once' && (
                                <>
                                <Stack direction="row" alignItems="center" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
                                  {formData.repeatType === 'weekly' && (
                                    <FormGroup row>
                                      {WEEKDAYS.map((d, i) => (
                                        <FormControlLabel
                                          key={d}
                                          sx={{ mr: 0.5 }}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={formData.repeatDays.includes(
                                                WEEKDAY_CRON[i] ?? 1
                                              )}
                                              onChange={(e) => {
                                                const cron = WEEKDAY_CRON[i] ?? 1;
                                                set(
                                                  'repeatDays',
                                                  e.target.checked
                                                    ? [...formData.repeatDays, cron]
                                                    : formData.repeatDays.filter((x) => x !== cron)
                                                );
                                              }}
                                            />
                                          }
                                          label={<Typography variant="caption">{d}</Typography>}
                                        />
                                      ))}
                                    </FormGroup>
                                  )}
                                  <TextField
                                    type="time"
                                    size="small"
                                    label="At what time"
                                    value={formData.repeatTime}
                                    onChange={(e) => set('repeatTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 140 }}
                                  />
                                  <TextField
                                    select
                                    size="small"
                                    label="Timezone"
                                    value={formData.repeatTimezone}
                                    onChange={(e) => set('repeatTimezone', e.target.value)}
                                    sx={{ minWidth: 200 }}
                                  >
                                    {CAMPAIGN_TIMEZONES.map((tz) => (
                                      <MenuItem key={tz.value} value={tz.value}>
                                        {tz.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={2} mt={1.5} flexWrap="wrap" useFlexGap>
                                  <TextField
                                    type="date"
                                    size="small"
                                    label="End date (optional)"
                                    value={formData.repeatEndDate}
                                    onChange={(e) => set('repeatEndDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Stop repeating after this date"
                                    sx={{ width: 200 }}
                                  />
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Max runs (optional)"
                                    value={formData.repeatMaxRuns}
                                    onChange={(e) =>
                                      set(
                                        'repeatMaxRuns',
                                        e.target.value === ''
                                          ? ''
                                          : Math.max(1, Number(e.target.value))
                                      )
                                    }
                                    inputProps={{ min: 1 }}
                                    helperText="Stop after this many sends"
                                    sx={{ width: 200 }}
                                  />
                                </Stack>
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', my: 0 }}
                    />
                  </Paper>

                  {/* Behavior-based */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: formData.sendMethod === 'behavior' ? 'primary.main' : 'divider',
                      bgcolor: formData.sendMethod === 'behavior' ? 'primary.50' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="behavior"
                      control={<Radio size="small" />}
                      label={
                        <Box width="100%">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <NotificationsActive fontSize="small" color="action" />
                            <Typography fontWeight={600}>Based on contact behavior</Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={0.25}
                            mb={formData.sendMethod === 'behavior' ? 1.5 : 0}
                          >
                            Automatically send when a contact goes quiet or hasn't replied.
                          </Typography>
                          {formData.sendMethod === 'behavior' && (
                            <Stack spacing={1.5} mt={0.5}>
                              <RadioGroup
                                value={formData.behaviorType}
                                onChange={(e) =>
                                  set('behaviorType', e.target.value as BehaviorType)
                                }
                              >
                                <FormControlLabel
                                  value="inactivity"
                                  control={<Radio size="small" />}
                                  label={
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Typography variant="body2">No activity for</Typography>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={formData.inactivityDays}
                                        onChange={(e) =>
                                          set('inactivityDays', parseInt(e.target.value) || 1)
                                        }
                                        inputProps={{ min: 1, max: 365, style: { width: 48 } }}
                                      />
                                      <Typography variant="body2">days</Typography>
                                    </Stack>
                                  }
                                />
                                <Tooltip
                                  arrow
                                  placement="right"
                                  title={
                                    followUpLocked
                                      ? `“No reply after” is locked — ${existingActiveFollowUp?.name} is already your active follow-up. Pause it first to set up a different one.`
                                      : ''
                                  }
                                >
                                  {/* span wrapper so the tooltip still fires while the
                                      control is disabled (disabled elements don't emit
                                      pointer events). */}
                                  <span style={{ display: 'inline-block' }}>
                                <FormControlLabel
                                  value="followup"
                                  disabled={followUpLocked}
                                  control={<Radio size="small" />}
                                  label={
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      flexWrap="wrap"
                                      useFlexGap
                                    >
                                      <Typography variant="body2">No reply after</Typography>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={formData.followupValue}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          set('followupValue', parseInt(e.target.value) || 1)
                                        }
                                        inputProps={{
                                          min: 1,
                                          max: formData.followupUnit === 'minutes' ? 1440 : 720,
                                          style: { width: 56 },
                                        }}
                                      />
                                      <TextField
                                        select
                                        size="small"
                                        value={formData.followupUnit}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          set('followupUnit', e.target.value as 'minutes' | 'hours')
                                        }
                                        sx={{ width: 120 }}
                                      >
                                        <MenuItem value="minutes">minutes</MenuItem>
                                        <MenuItem value="hours">hours</MenuItem>
                                      </TextField>
                                    </Stack>
                                  }
                                />
                                  </span>
                                </Tooltip>
                              </RadioGroup>

                              {/* Follow-up detail panel. Rendered as its own bordered
                                  card and its clicks stop-propagated so interacting
                                  with the inner radios/inputs never toggles the outer
                                  send-method radio (they all live inside its <label>). */}
                              {formData.behaviorType === 'followup' && (
                                <Box
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    ml: 3.5,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      color: 'text.secondary',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Apply to
                                  </Typography>
                                  <RadioGroup
                                    value={formData.followupScope}
                                    onChange={(e) =>
                                      set('followupScope', e.target.value as 'new' | 'all')
                                    }
                                    sx={{ mt: 0.5 }}
                                  >
                                    <FormControlLabel
                                      value="new"
                                      control={<Radio size="small" sx={{ py: 0.5 }} />}
                                      sx={{ alignItems: 'flex-start', mb: 0.5 }}
                                      label={
                                        <Box sx={{ pt: 0.25 }}>
                                          <Typography variant="body2">
                                            New conversations only
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Recommended — only chats that go quiet from now on; never
                                            messages your existing backlog.
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    <FormControlLabel
                                      value="all"
                                      control={<Radio size="small" sx={{ py: 0.5 }} />}
                                      sx={{ alignItems: 'flex-start' }}
                                      label={
                                        <Box sx={{ pt: 0.25 }}>
                                          <Typography variant="body2">All conversations</Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Includes older chats that are already quiet.
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </RadioGroup>

                                  <Divider sx={{ my: 1.5 }} />

                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Typography variant="body2">Send at most</Typography>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={formData.followupMaxAttempts}
                                      onChange={(e) =>
                                        set('followupMaxAttempts', parseInt(e.target.value) || 1)
                                      }
                                      inputProps={{ min: 1, max: 5, style: { width: 48 } }}
                                    />
                                    <Typography variant="body2">
                                      follow-up{formData.followupMaxAttempts === 1 ? '' : 's'} per
                                      contact
                                    </Typography>
                                  </Stack>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 1.5 }}
                                  >
                                    Only one follow-up campaign can be active per workspace at a
                                    time. Pause the current one before activating another.
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          )}
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', my: 0 }}
                    />
                  </Paper>
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider />

            {/* ── Auto-retry failed deliveries (Tier 3) ──────────────────── */}
            {/* Deliberately NOT inside the send-method radio group above (and not
                repeated per option) — retry applies to every run this campaign
                produces, whether it's sent manually, on a schedule, or by a
                repeating schedule. One setting for the whole campaign, not a
                per-send-method toggle. */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} display="block" mb={0.5}>
                After it sends
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.retryEnabled}
                    onChange={(e) => set('retryEnabled', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={600} variant="body2">
                      Automatically retry failed deliveries
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applies no matter how this campaign is sent — manually, on a schedule, or
                      repeating. Re-sends only messages that failed for a temporary reason (mainly
                      WhatsApp frequency-capping), spaced hours apart so the block can clear.
                      Permanent failures (invalid number, blocked, template issues) are never retried.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
              {formData.retryEnabled && (
                <Box sx={{ pl: 4, pt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Each retry re-sends only the recipients still eligible, timed from when the
                    previous run/retry finished. Up to 3 rounds.
                  </Typography>
                  <Stack spacing={1}>
                    {formData.retryDelaysMinutes.map((mins, i) => {
                      const { hours, minutes } = minutesToHm(mins);
                      return (
                        <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                          <Typography variant="body2" sx={{ width: 64, flexShrink: 0 }}>
                            Retry #{i + 1}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>
                            {i === 0 ? 'after campaign completes' : `after retry #${i}`}
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            label="Hours"
                            value={hours}
                            onChange={(e) => {
                              const next = [...formData.retryDelaysMinutes];
                              next[i] = hmToMinutes(Number(e.target.value), minutes);
                              set('retryDelaysMinutes', next);
                            }}
                            inputProps={{ min: 0, max: 168, style: { width: 56 } }}
                          />
                          <TextField
                            type="number"
                            size="small"
                            label="Minutes"
                            value={minutes}
                            onChange={(e) => {
                              const next = [...formData.retryDelaysMinutes];
                              next[i] = hmToMinutes(hours, Number(e.target.value));
                              set('retryDelaysMinutes', next);
                            }}
                            inputProps={{ min: 0, max: 59, style: { width: 56 } }}
                          />
                          {formData.retryDelaysMinutes.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const next = formData.retryDelaysMinutes.filter((_, j) => j !== i);
                                set('retryDelaysMinutes', next);
                                set('retryMaxRetries', next.length);
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                  {formData.retryDelaysMinutes.length < 3 && (
                    <Button
                      size="small"
                      startIcon={<Add fontSize="small" />}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const next = [
                          ...formData.retryDelaysMinutes,
                          defaultNextRetryDelay(formData.retryDelaysMinutes),
                        ];
                        set('retryDelaysMinutes', next);
                        set('retryMaxRetries', next.length);
                      }}
                    >
                      Add retry round
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        )}

        {/* ── Tab 2: Recipient Filters (who receives it) ───────────────────── */}
        {tab === 2 && (
          <Stack spacing={3}>
            {/* ── Who receives it ────────────────────────────────────────── */}
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
                flexWrap="wrap"
                gap={1}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Who receives this campaign?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define your audience — then optionally narrow it down below.
                  </Typography>
                </Box>
                <Chip
                  icon={<GroupsOutlined />}
                  color={reach ? 'primary' : 'default'}
                  variant={reach ? 'filled' : 'outlined'}
                  label={
                    reachLoading
                      ? 'Estimating reach…'
                      : reach !== null
                        ? `Reaches ~${reach.toLocaleString()} contact${reach === 1 ? '' : 's'}`
                        : 'Reach unavailable'
                  }
                />
              </Stack>

              <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                Contacts who've unsubscribed (replied STOP or opted out via WhatsApp) are never
                included, regardless of the filters below — this campaign won't reach them.
              </Alert>

              <Grid container spacing={3} mt={0.5}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <StepHeader
                      step={1}
                      title="Pick a base audience"
                      description="Every filter below narrows this down further — it never widens it."
                    />
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Recipients"
                      value={formData.targetFilter}
                      onChange={(e) => set('targetFilter', e.target.value)}
                      sx={{ maxWidth: 360 }}
                    >
                      <MenuItem value="all">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <People fontSize="small" color="action" />
                          <span>All contacts</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="agent">Contacts handled by a specific agent</MenuItem>
                      <MenuItem value="tag">Contacts with specific tags</MenuItem>
                      <MenuItem value="country">Contacts in specific countries</MenuItem>
                      <MenuItem value="segment">Custom segment (combine filters)</MenuItem>
                      <MenuItem value="savedSegment">Saved segment (from Contacts)</MenuItem>
                      <MenuItem value="contacts">Specific contacts (choose from existing)</MenuItem>
                    </TextField>

                    {formData.targetFilter === 'all' && (
                      <Typography variant="caption" color="text.secondary">
                        Every contact in your account will receive this campaign.
                      </Typography>
                    )}

                    {formData.targetFilter === 'agent' && (
                      <TextField
                        select
                        size="small"
                        label="Agent"
                        value={formData.targetAgentId}
                        onChange={(e) => set('targetAgentId', e.target.value)}
                        sx={{ maxWidth: 320 }}
                      >
                        {agents.length === 0 && <MenuItem value="">No agents found</MenuItem>}
                        {agents.map((a) => (
                          <MenuItem key={a._id} value={a._id}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                    {formData.targetFilter === 'tag' && (
                      <Box>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          display="block"
                          mb={0.75}
                        >
                          Tags
                        </Typography>
                        {segmentOptions.tags.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">
                            No tags found yet.
                          </Typography>
                        ) : (
                          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                            {segmentOptions.tags.map((tag) => {
                              const selected = formData.targetTagList.includes(tag);
                              return (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  color={selected ? 'primary' : 'default'}
                                  variant={selected ? 'filled' : 'outlined'}
                                  onClick={() =>
                                    setFormData((p) => ({
                                      ...p,
                                      targetTagList: selected
                                        ? p.targetTagList.filter((t) => t !== tag)
                                        : [...p.targetTagList, tag],
                                    }))
                                  }
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    )}

                    {formData.targetFilter === 'country' && (
                      <TextField
                        size="small"
                        label="Country codes (comma-separated)"
                        value={formData.targetCountries}
                        onChange={(e) => set('targetCountries', e.target.value)}
                        placeholder="IN, US, GB"
                        sx={{ maxWidth: 320 }}
                      />
                    )}

                    {formData.targetFilter === 'contacts' && (
                      <Autocomplete
                        multiple
                        fullWidth
                        size="small"
                        options={contactOptions}
                        loading={contactSearchLoading}
                        value={formData.targetContacts}
                        isOptionEqualToValue={(a, b) => a.phone === b.phone}
                        getOptionLabel={(c) => (c.name ? `${c.name} (${c.phone})` : c.phone)}
                        filterOptions={(x) => x}
                        onInputChange={(_, value) => setContactSearch(value)}
                        onChange={(_, value) => set('targetContacts', value)}
                        renderTags={(value, getTagProps) =>
                          value.map((c, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={c.phone}
                              size="small"
                              label={c.name ? `${c.name} (${c.phone})` : c.phone}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...(params as any)}
                            label="Search contacts by name or phone"
                            placeholder="Start typing to search…"
                            helperText={
                              formData.targetContacts.length === 0
                                ? "No contacts picked yet — reach will show 0 until you add someone here."
                                : 'Only contacts you select here will receive this campaign.'
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {contactSearchLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}

                    {formData.targetFilter === 'segment' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                          Only contacts matching ALL selected filters below will receive this
                          campaign.
                        </Typography>
                        <Stack spacing={2}>
                          {/* Tags */}
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={0.75}
                            >
                              <Typography variant="overline" color="text.secondary">
                                Tags
                              </Typography>
                              {segmentOptions.tags.length > 0 && (
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ minWidth: 0, py: 0 }}
                                  onClick={() =>
                                    set(
                                      'segmentTags',
                                      formData.segmentTags.length === segmentOptions.tags.length
                                        ? []
                                        : [...segmentOptions.tags]
                                    )
                                  }
                                >
                                  {formData.segmentTags.length === segmentOptions.tags.length
                                    ? 'Clear all'
                                    : 'Select all'}
                                </Button>
                              )}
                            </Stack>
                            {segmentOptions.tags.length === 0 ? (
                              <Typography variant="caption" color="text.disabled">
                                No tags found yet.
                              </Typography>
                            ) : (
                              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                                {segmentOptions.tags.map((tag) => {
                                  const selected = formData.segmentTags.includes(tag);
                                  return (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      color={selected ? 'primary' : 'default'}
                                      variant={selected ? 'filled' : 'outlined'}
                                      onClick={() => toggleListValue('segmentTags', tag, !selected)}
                                    />
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>

                          {/* Country codes */}
                          <Box>
                            <Typography
                              variant="overline"
                              color="text.secondary"
                              display="block"
                              mb={0.75}
                            >
                              Countries
                            </Typography>
                            {segmentOptions.countryCodes.length === 0 ? (
                              <Typography variant="caption" color="text.disabled">
                                No countries found yet.
                              </Typography>
                            ) : (
                              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                                {segmentOptions.countryCodes.map((code) => {
                                  const selected = formData.segmentCountries.includes(code);
                                  return (
                                    <Chip
                                      key={code}
                                      label={code}
                                      size="small"
                                      color={selected ? 'primary' : 'default'}
                                      variant={selected ? 'filled' : 'outlined'}
                                      onClick={() =>
                                        toggleListValue('segmentCountries', code, !selected)
                                      }
                                    />
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>

                          {/* Conversation status + Agent */}
                          <Stack direction="row" spacing={2}>
                            <TextField
                              select
                              size="small"
                              label="Conversation status"
                              value={formData.segmentStatus}
                              onChange={(e) => set('segmentStatus', e.target.value)}
                              sx={{ minWidth: 180 }}
                            >
                              <MenuItem value="">Any status</MenuItem>
                              {segmentOptions.statuses.map((s) => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                                  {s}
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              select
                              size="small"
                              label="Handled by agent"
                              value={formData.segmentAgent}
                              onChange={(e) => set('segmentAgent', e.target.value)}
                              sx={{ minWidth: 200 }}
                            >
                              <MenuItem value="">Any agent</MenuItem>
                              {segmentOptions.agents.map((a) => (
                                <MenuItem key={a._id} value={a._id}>
                                  {a.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>

                          {/* Custom fields */}
                          <Box>
                            <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                              Custom fields
                            </Typography>
                            <Stack spacing={1}>
                              {formData.segmentCustomAttributes.map((field, index) => (
                                <Stack
                                  key={index}
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                >
                                  <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={segmentOptions.customAttributeKeys}
                                    value={field.key}
                                    onChange={(_, value) =>
                                      updateSegmentCustomAttribute(index, { key: value || '' })
                                    }
                                    onInputChange={(_, value, reason) => {
                                      if (reason === 'input')
                                        updateSegmentCustomAttribute(index, { key: value });
                                    }}
                                    sx={{ minWidth: 180 }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...(params as any)}
                                        label="Field name"
                                        placeholder="e.g. Plan"
                                      />
                                    )}
                                  />
                                  <TextField
                                    size="small"
                                    label="Value"
                                    sx={{ minWidth: 160 }}
                                    value={field.value}
                                    onChange={(e) =>
                                      updateSegmentCustomAttribute(index, { value: e.target.value })
                                    }
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => removeSegmentCustomAttribute(index)}
                                    sx={{ mt: 0.5 }}
                                    aria-label="Remove condition"
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ))}
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<Add />}
                                onClick={addSegmentCustomAttribute}
                                sx={{ alignSelf: 'flex-start' }}
                              >
                                Add condition
                              </Button>
                            </Stack>
                          </Box>

                          {/* Tier 1 — WhatsApp delivery/engagement filter */}
                          <Box>
                            <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                              WhatsApp delivery status
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <TextField
                                select
                                size="small"
                                label="Status"
                                value={formData.segmentAdvanced}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setFormData((p) => ({
                                    ...p,
                                    segmentAdvanced: next,
                                    ...(next !== 'failedByError'
                                      ? { segmentAdvancedErrorCode: '' }
                                      : {}),
                                    ...(next !== 'neverDeliveredThisCampaign'
                                      ? { segmentAdvancedCampaignId: '' }
                                      : {}),
                                  }));
                                }}
                                sx={{ minWidth: 220 }}
                              >
                                {ADVANCED_FILTER_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              {formData.segmentAdvanced === 'failedByError' && (
                                <TextField
                                  size="small"
                                  label="Error code"
                                  placeholder="e.g. 131026"
                                  value={formData.segmentAdvancedErrorCode}
                                  onChange={(e) =>
                                    set(
                                      'segmentAdvancedErrorCode',
                                      e.target.value.replace(/[^0-9]/g, '')
                                    )
                                  }
                                  inputProps={{ inputMode: 'numeric' }}
                                  sx={{ minWidth: 160 }}
                                />
                              )}
                              {formData.segmentAdvanced === 'neverDeliveredThisCampaign' && (
                                <TextField
                                  select
                                  size="small"
                                  label="Campaign"
                                  value={formData.segmentAdvancedCampaignId}
                                  onChange={(e) => set('segmentAdvancedCampaignId', e.target.value)}
                                  sx={{ minWidth: 220 }}
                                >
                                  <MenuItem value="">
                                    <em>
                                      {otherCampaigns.length
                                        ? 'Select a campaign…'
                                        : 'No other campaigns yet'}
                                    </em>
                                  </MenuItem>
                                  {otherCampaigns.map((c) => (
                                    <MenuItem key={c._id} value={c._id}>
                                      {c.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              Combined with every other condition above (AND). Based on this
                              tenant's own WhatsApp send/read history — not Meta's account-level
                              quality rating.
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {formData.targetFilter === 'savedSegment' && (
                      <Box>
                        {savedSegments.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">
                            No saved segments yet — build and save one from the Contacts page's
                            Filters drawer, then it'll show up here.
                          </Typography>
                        ) : (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Segment"
                            value={formData.targetSavedSegmentId}
                            onChange={(e) => set('targetSavedSegmentId', e.target.value)}
                            sx={{ maxWidth: 360 }}
                          >
                            {savedSegments.map((seg) => (
                              <MenuItem key={seg._id} value={seg._id}>
                                <Stack>
                                  <span>{seg.name}</span>
                                  {seg.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {seg.description}
                                    </Typography>
                                  )}
                                </Stack>
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          Re-resolved live at send time — if the segment's definition changes later
                          (or contacts newly match/stop matching it), this campaign always targets
                          whoever matches right now, not a fixed list captured today.
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  {/* Steps 2-4 share one compact card so the tab reads as a short list of
                      small dropdown-style rows rather than three separate boxy panels. */}
                  <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                    {/* Audience slice — order/limit/reach-status on top of the filter above.
                    Hidden for recurring campaigns (see isRecurringCampaign). */}
                    {!isRecurringCampaign && (
                      <Box sx={{ p: 2 }}>
                        <StepHeader
                          step={2}
                          title="Narrow it down"
                          description="Pick a subset of the contacts matched above — e.g. just the newest 50, or only people this campaign hasn't messaged yet."
                          optional
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                          <TextField
                            select
                            size="small"
                            label="Who to include"
                            value={formData.audienceReachStatus}
                            onChange={(e) => set('audienceReachStatus', e.target.value)}
                            sx={{ minWidth: 220, flex: '1 1 220px' }}
                            helperText={
                              formData.audienceReachStatus === 'neverTargeted' ||
                              formData.audienceReachStatus === 'targetedAny'
                                ? "\"Targeted\" = a send was attempted, even if it later failed"
                                : ' '
                            }
                          >
                            {CAMPAIGN_REACH_STATUS_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Order"
                            value={formData.audienceSendOrder}
                            onChange={(e) => set('audienceSendOrder', e.target.value)}
                            sx={{ minWidth: 180, flex: '1 1 180px' }}
                          >
                            {AUDIENCE_SEND_ORDER_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            size="small"
                            type="number"
                            label="How many contacts"
                            value={formData.audienceLimit}
                            onChange={(e) =>
                              set('audienceLimit', e.target.value.replace(/[^0-9]/g, ''))
                            }
                            placeholder="Everyone matched"
                            error={
                              formData.audienceSendOrder === 'random' &&
                              !(Number(formData.audienceLimit) > 0)
                            }
                            helperText={
                              formData.audienceSendOrder === 'random'
                                ? 'Required when picking randomly'
                                : ' '
                            }
                            inputProps={{ min: 1 }}
                            sx={{ minWidth: 170, flex: '1 1 170px' }}
                          />
                        </Stack>

                        {/* "Skip" only matters for sending a saved slice in sequential
                            batches (e.g. "I already sent to the first 50, now send the
                            next 50") — rare enough to keep out of the way by default. */}
                        {formData.audienceSendOrder !== 'random' && (
                          <Box mt={1}>
                            {!formData.showAudienceAdvanced ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => set('showAudienceAdvanced', true)}
                                sx={{ minWidth: 0, px: 0 }}
                              >
                                + Already sent to some of these? Start further down the list
                              </Button>
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                label="Start after this many contacts"
                                value={formData.audienceOffset}
                                onChange={(e) =>
                                  set('audienceOffset', e.target.value.replace(/[^0-9]/g, ''))
                                }
                                placeholder="0"
                                helperText="e.g. enter 50 if you already sent to the first 50"
                                inputProps={{ min: 0 }}
                                sx={{ minWidth: 260, mt: 0.5 }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {!isRecurringCampaign && <Divider />}

                    {/* Exclude tags */}
                    <Box sx={{ p: 2 }}>
                      <StepHeader step={3} title="Exclude certain contacts" optional />
                      <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={segmentOptions.tags}
                        value={formData.excludeTags}
                        onChange={(_, value) => set('excludeTags', value as string[])}
                        sx={{ maxWidth: 420 }}
                        renderTags={(value, getTagProps) =>
                          value.map((tag, index) => (
                            <Chip {...getTagProps({ index })} key={tag} size="small" label={tag} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...(params as any)}
                            label="Skip contacts tagged with…"
                            placeholder={formData.excludeTags.length ? '' : 'e.g. opt-out, do-not-contact, vip'}
                            helperText="Pick from your existing tags, or type a new one and press Enter."
                          />
                        )}
                      />
                    </Box>

                    <Divider />

                    {/* Safety cap — independent of the slice limit above. */}
                    <Box sx={{ p: 2 }}>
                      <StepHeader step={4} title="Set a safety cap" optional />
                      <TextField
                        size="small"
                        label="Maximum recipients"
                        type="number"
                        value={formData.maxRecipients}
                        onChange={(e) => set('maxRecipients', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 500"
                        helperText="We'll stop once this many people are reached, even if more contacts match. Leave blank to use your plan's default."
                        inputProps={{ min: 1 }}
                        sx={{ minWidth: 260 }}
                      />
                      <TextField
                        size="small"
                        label="Daily send limit"
                        type="number"
                        value={formData.dailyLimit}
                        onChange={(e) => set('dailyLimit', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 200"
                        helperText="Max messages this campaign sends per day. Leave blank to use your plan's daily limit."
                        inputProps={{ min: 1 }}
                        sx={{ minWidth: 260, mt: 2 }}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* ── Segment breakdown — live summary of the filters at left ── */}
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, position: { md: 'sticky' }, top: { md: 16 } }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                      display="block"
                      mb={1.5}
                    >
                      Segment breakdown
                    </Typography>

                    <Stack spacing={1.25}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="body2" color="text.secondary">
                          Total database
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {totalDatabaseCount !== null ? totalDatabaseCount.toLocaleString() : '—'}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={
                          totalDatabaseCount && reach
                            ? Math.min(100, (reach / totalDatabaseCount) * 100)
                            : 0
                        }
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          Estimated match
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="primary.main">
                          {reachLoading ? '…' : reach !== null ? reach.toLocaleString() : '—'}
                        </Typography>
                      </Stack>

                      <Divider sx={{ my: 0.5 }} />

                      {(
                        [
                          [
                            'Recipient type',
                            RECIPIENT_TYPE_LABELS[formData.targetFilter] || formData.targetFilter,
                          ],
                          // "Specific contacts" has no tags/countries to summarize — showing
                          // "Selected tags: 0" there would misleadingly look like a filter
                          // problem when it's really just an empty picker. Show how many
                          // contacts have actually been picked instead.
                          ...(formData.targetFilter === 'contacts'
                            ? [['Contacts selected', String(formData.targetContacts.length)]]
                            : [
                                [
                                  'Selected tags',
                                  String(
                                    formData.targetFilter === 'tag'
                                      ? formData.targetTagList.length
                                      : formData.targetFilter === 'segment'
                                        ? formData.segmentTags.length
                                        : 0
                                  ),
                                ],
                                [
                                  'Countries',
                                  String(
                                    formData.targetFilter === 'segment'
                                      ? formData.segmentCountries.length
                                      : formData.targetFilter === 'country'
                                        ? formData.targetCountries
                                            .split(',')
                                            .map((s) => s.trim())
                                            .filter(Boolean).length
                                        : 0
                                  ),
                                ],
                              ]),
                          ['How many contacts', formData.audienceLimit || 'Everyone matched'],
                          ['Maximum recipients', formData.maxRecipients || 'Plan default'],
                          ['Daily send limit', formData.dailyLimit || 'Plan default'],
                        ] as Array<[string, string]>
                      ).map(([label, value]) => (
                        <Stack key={label} direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {label}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {value}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Box mt={2.5} p={1.5} sx={{ bgcolor: 'primary.50', borderRadius: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <AutoAwesome fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                        <Box>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="primary.main"
                            display="block"
                          >
                            TIP
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Use <b>Narrow this send</b> to stagger deliveries — great for testing
                            template performance before scaling.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        )}

        {/* Inline form errors/success */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <CheckCircleOutline fontSize="small" sx={{ mr: 1 }} />
            {success}
          </Alert>
        )}

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <Stack direction="row" justifyContent="space-between" spacing={1.5} mt={3}>
          <Box>
            {tab > 0 && (
              <Button onClick={() => setTab((tab - 1) as 0 | 1 | 2)}>← Back</Button>
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={() => navigate('/admin/campaigns')}>Cancel</Button>
            {tab === 0 && (
              <Button variant="outlined" onClick={() => setTab(1)}>
                Next: Triggers →
              </Button>
            )}
            {tab === 1 && (
              <Button variant="outlined" onClick={() => setTab(2)}>
                Next: Recipient Filters →
              </Button>
            )}
            {/* The Save button is reachable from every tab, not just the last —
                a user who's already filled everything shouldn't be forced to
                click through tabs just to submit. */}
            <Button
              variant="contained"
              onClick={guard('campaignsEnabled', handleSubmit)}
              disabled={!canSubmit || submitting}
            >
              {editMode ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CampaignForm;
